"use client";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { AllStoriesSortOption, TimeRangeFilter } from "../types";

interface StoriesToolbarProps {
  sort: AllStoriesSortOption;
  timeRange: TimeRangeFilter;
  onSortChange: (sort: AllStoriesSortOption) => void;
  onTimeRangeChange: (value: TimeRangeFilter) => void;
  /** When true, hide the Sort dropdown (e.g. in search mode where Relevance is the only sort). */
  hideSort?: boolean;
}

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "trending", label: "Trending" },
] satisfies Array<{
  value: AllStoriesSortOption;
  label: string;
}>;

const TIME_OPTIONS = [
  { value: "48h", label: "48h" },
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "all", label: "All" },
] satisfies Array<{ value: TimeRangeFilter; label: string }>;

const dropdownContentClassName =
  "border-[var(--explore-border-light)] bg-[var(--explore-bg-sidebar)] shadow-md";

const dropdownItemClassName =
  "font-medium text-[0.78rem] text-[var(--explore-text-secondary)] focus:bg-[var(--explore-border-light)] focus:text-[var(--explore-text-primary)]";

const selectTriggerClassName =
  "h-auto w-auto shrink-0 gap-1 border-transparent bg-transparent px-0 py-0 font-medium text-[0.78rem] text-[var(--explore-text-secondary)] shadow-none";

function ToolbarDivider() {
  return (
    <div className="hidden h-4 w-px bg-[var(--explore-border)] lg:block" />
  );
}

export function StoriesToolbar({
  sort,
  timeRange,
  onSortChange,
  onTimeRangeChange,
  hideSort = false,
}: StoriesToolbarProps) {
  return (
    <div className="flex items-baseline gap-2 lg:gap-3">
      {hideSort ? (
        <span className="hidden text-[0.78rem] text-[var(--explore-text-muted)] lg:inline">
          Sort:{" "}
          <span className="text-[var(--explore-text-secondary)]">
            Relevance
          </span>
        </span>
      ) : (
        <>
          {/* Sort */}
          <div className="flex items-center gap-1.5">
            <span className="hidden text-[0.78rem] text-[var(--explore-text-muted)] lg:inline">
              Sort:
            </span>
            <Select onValueChange={onSortChange} value={sort}>
              <SelectTrigger className={selectTriggerClassName}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent
                className={`min-w-[140px] ${dropdownContentClassName}`}
                style={{ fontFamily: "var(--homepage-sans-stack)" }}
              >
                {SORT_OPTIONS.map(({ value, label }) => (
                  <SelectItem
                    className={dropdownItemClassName}
                    key={value}
                    value={value}
                  >
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      <ToolbarDivider />

      {/* Time — dropdown on mobile, inline buttons on desktop */}
      <div className="flex items-center gap-0.5">
        {/* Mobile: dropdown */}
        <div className="lg:hidden">
          <Select onValueChange={onTimeRangeChange} value={timeRange}>
            <SelectTrigger className={selectTriggerClassName}>
              <span className="mr-0.5 text-[var(--explore-text-muted)]">
                Time
              </span>
            </SelectTrigger>
            <SelectContent
              className={`min-w-[80px] ${dropdownContentClassName}`}
              style={{ fontFamily: "var(--homepage-sans-stack)" }}
            >
              {TIME_OPTIONS.map(({ value, label }) => (
                <SelectItem
                  className={dropdownItemClassName}
                  key={value}
                  value={value}
                >
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Desktop: label + inline buttons */}
        <div className="hidden items-center gap-0.5 lg:flex">
          <span className="mr-1 text-[0.78rem] text-[var(--explore-text-muted)]">
            Time:
          </span>
          {TIME_OPTIONS.map(({ value, label }) => (
            <button
              className={cn(
                "border-none bg-transparent px-1.5 py-0.5 font-medium text-[0.78rem] transition-colors",
                timeRange === value
                  ? "text-[var(--explore-text-primary)]"
                  : "text-[var(--explore-text-muted)] hover:text-[var(--explore-text-secondary)]"
              )}
              key={value}
              onClick={() => onTimeRangeChange(value)}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
